import { lazy, Suspense } from "react";
import { useNavigate } from "react-router";
import { EmptyState, Pagination } from "@/shared/components";
import { useContractorPayrollListPage } from "../../hooks";
import { ContractorPayrollHeader, ContractorPayrollFilter, ContractorPayrollPageSkeleton } from "./components";

const ContractorPayrollTable = lazy(() =>
  import("./components/ContractorPayrollTable").then((m) => ({ default: m.default })),
);

export default function ContractorPayrollListPage() {
  const navigate = useNavigate();

  const {
    data,
    isLoading,
    isError,
    filteredItems,
    pagination,
    pageItems,
    sortConfig,
    handleSort,
    search,
    setSearch,
    dateRange,
    setDateRange,
    statusFilter,
    setStatusFilter,
    statusOptions,
    activeFilters,
    handleResetFilters,
    locale,
    t,
  } = useContractorPayrollListPage();

  if (isLoading && !data) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <ContractorPayrollHeader />
          <ContractorPayrollPageSkeleton />
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1">
          <ContractorPayrollHeader />
          <p className="mt-4 text-body-regular-md content-action-negative">{t("messages.error")}</p>
        </section>
      </>
    );
  }

  function handleRowClick(year: number, month: number) {
    navigate(`/accounting/contractor-payroll/detail?year=${year}&month=${month}`);
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1">
        <ContractorPayrollHeader />

        <div className="radius-3xl surface-base-fill p-7">
          <ContractorPayrollFilter
            search={search}
            onSearchChange={setSearch}
            dateRange={dateRange}
            onDateRangeChange={setDateRange}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            statusOptions={statusOptions}
            locale={locale}
            hasActiveFilters={activeFilters}
            onReset={handleResetFilters}
          />

          {filteredItems.length === 0 ? (
            <EmptyState
              hasActiveFilters={activeFilters}
              noResultsText={t("table.noResults")}
              noDataText={t("table.noData")}
              noDataHint={t("table.noDataHint")}
            />
          ) : (
            <>
              <Suspense fallback={<ContractorPayrollPageSkeleton variant="tableOnly" />}>
                <ContractorPayrollTable
                  items={pageItems}
                  onOpen={handleRowClick}
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
              </Suspense>

              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={pagination.setPage}
                fromItem={pagination.fromItem}
                toItem={pagination.toItem}
                total={filteredItems.length}
              />
            </>
          )}
        </div>
      </section>
    </>
  );
}
